import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    Button,
    TextField,
    Grid,
    Card,
    CardContent,
    Alert,
    CircularProgress,
    InputAdornment,
    MenuItem,
    Autocomplete,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton
} from '@mui/material';
import {
    Search as SearchIcon,
    Payment as PaymentIcon,
    Receipt as ReceiptIcon,
    CheckCircle as CheckCircleIcon,
    Refresh as RefreshIcon,
    Visibility as VisibilityIcon,
    CreditCard as CreditCardIcon
} from '@mui/icons-material';
import { getCreditCustomers, recordPayment, getCreditTransactions } from '../../services/api';
import './Credit.css';

const PaymentCollection = () => {
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerDetails, setCustomerDetails] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingCustomer, setLoadingCustomer] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    
    const [paymentData, setPaymentData] = useState({
        amount: '',
        payment_method: 'cash',
        cheque_number: '',
        bank_name: '',
        description: ''
    });

    const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
    const [lastPayment, setLastPayment] = useState(null);

    useEffect(() => {
        fetchCreditCustomers();
    }, []);

    useEffect(() => {
        if (selectedCustomer) {
            fetchCustomerDetails();
        }
    }, [selectedCustomer]);

    const fetchCreditCustomers = async () => {
        setLoading(true);
        try {
            const response = await getCreditCustomers();
            setCustomers(response.data);
        } catch (err) {
            setError(err.message || 'Failed to load customers');
        } finally {
            setLoading(false);
        }
    };

    const fetchCustomerDetails = async () => {
        if (!selectedCustomer) return;
        
        setLoadingCustomer(true);
        try {
            // Fetch customer details and transactions
            const [transactionsRes] = await Promise.all([
                getCreditTransactions(selectedCustomer.id, { limit: 10 })
            ]);
            
            setCustomerDetails(selectedCustomer);
            setTransactions(transactionsRes.data.transactions || []);
            
            // Set payment amount to outstanding balance
            setPaymentData(prev => ({
                ...prev,
                amount: selectedCustomer.outstanding_balance.toString().replace(/[^0-9.-]+/g, "")
            }));
        } catch (err) {
            setError(err.message || 'Failed to load customer details');
        } finally {
            setLoadingCustomer(false);
        }
    };

    const handlePaymentSubmit = async () => {
        if (!selectedCustomer || !paymentData.amount) {
            setError('Please select a customer and enter payment amount');
            return;
        }

        setLoading(true);
        try {
            const response = await recordPayment({
                customer_id: selectedCustomer.id,
                amount: parseFloat(paymentData.amount),
                payment_method: paymentData.payment_method,
                cheque_number: paymentData.cheque_number || null,
                bank_name: paymentData.bank_name || null,
                description: paymentData.description || `Payment received from ${selectedCustomer.name}`
            });

            setLastPayment(response.data);
            setSuccess('Payment recorded successfully!');
            
            // Reset form
            setPaymentData({
                amount: '',
                payment_method: 'cash',
                cheque_number: '',
                bank_name: '',
                description: ''
            });
            
            // Refresh customer details
            await fetchCustomerDetails();
            
            // Show receipt dialog
            setReceiptDialogOpen(true);
            
        } catch (err) {
            setError(err.message || 'Failed to record payment');
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        if (typeof amount === 'string' && amount.includes('LKR')) return amount;
        return new Intl.NumberFormat('en-LK', {
            style: 'currency',
            currency: 'LKR',
            minimumFractionDigits: 2
        }).format(parseFloat(amount) || 0);
    };

    const printReceipt = () => {
        const receiptWindow = window.open('', '_blank');
        receiptWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Payment Receipt</title>
                <style>
                    body { font-family: 'Courier New', monospace; font-size: 12px; padding: 20px; max-width: 80mm; margin: 0 auto; }
                    .header { text-align: center; margin-bottom: 20px; }
                    .receipt-title { font-size: 18px; font-weight: bold; margin-bottom: 5px; }
                    .divider { border-top: 1px dashed #000; margin: 10px 0; }
                    table { width: 100%; border-collapse: collapse; }
                    th { text-align: left; padding: 5px 0; border-bottom: 1px solid #ddd; }
                    td { padding: 3px 0; }
                    .right { text-align: right; }
                    .total-row { font-weight: bold; border-top: 2px solid #000; }
                    .footer { text-align: center; margin-top: 20px; font-size: 10px; color: #666; }
                </style>
            </head>
            <body>
                <div class="header">
                    <div class="receipt-title">PAYMENT RECEIPT</div>
                    <div>Date: ${new Date().toLocaleDateString()}</div>
                    <div>Time: ${new Date().toLocaleTimeString()}</div>
                </div>
                
                <div class="divider"></div>
                
                <div style="margin-bottom: 10px;">
                    <strong>Reference:</strong> ${lastPayment?.transaction?.reference_number || 'N/A'}<br>
                    <strong>Customer:</strong> ${lastPayment?.customer?.name || selectedCustomer?.name}<br>
                    <strong>Collected By:</strong> ${localStorage.getItem('erp_username') || 'User'}
                </div>
                
                <div class="divider"></div>
                
                <table>
                    <tr>
                        <td>Payment Amount:</td>
                        <td class="right"><strong>${formatCurrency(paymentData.amount)}</strong></td>
                    </tr>
                    <tr>
                        <td>Payment Method:</td>
                        <td class="right">${paymentData.payment_method.toUpperCase()}</td>
                    </tr>
                    ${paymentData.cheque_number ? `
                        <tr>
                            <td>Cheque Number:</td>
                            <td class="right">${paymentData.cheque_number}</td>
                        </tr>
                    ` : ''}
                    ${paymentData.bank_name ? `
                        <tr>
                            <td>Bank:</td>
                            <td class="right">${paymentData.bank_name}</td>
                        </tr>
                    ` : ''}
                </table>
                
                <div class="divider"></div>
                
                ${lastPayment?.credit_summary ? `
                    <table>
                        <tr>
                            <td>Previous Balance:</td>
                            <td class="right">${lastPayment.credit_summary.outstanding_balance}</td>
                        </tr>
                        <tr>
                            <td>Payment Made:</td>
                            <td class="right">-${formatCurrency(paymentData.amount)}</td>
                        </tr>
                        <tr class="total-row">
                            <td>New Balance:</td>
                            <td class="right">${formatCurrency(
                                parseFloat(lastPayment.credit_summary.outstanding_balance.replace(/[^0-9.-]+/g, "")) - 
                                parseFloat(paymentData.amount)
                            )}</td>
                        </tr>
                    </table>
                ` : ''}
                
                <div class="divider"></div>
                
                <div style="margin: 15px 0; text-align: center;">
                    <div style="font-size: 24px;">✓</div>
                    <div>PAYMENT CONFIRMED</div>
                </div>
                
                <div class="footer">
                    Thank you for your payment!<br>
                    Keep this receipt for your records<br>
                    ERP Credit System
                </div>
                
                <script>
                    window.onload = function() {
                        window.print();
                        setTimeout(function() {
                            window.close();
                        }, 3000);
                    }
                </script>
            </body>
            </html>
        `);
    };

    if (loading && !selectedCustomer) {
        return (
            <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress />
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" component="h1">
                    <PaymentIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
                    Payment Collection
                </Typography>
                <Typography variant="subtitle1" color="textSecondary">
                    Collect payments from credit customers
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            <Grid container spacing={3}>
                {/* Left Column - Customer Selection & Payment Form */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Select Customer
                        </Typography>
                        
                        <Autocomplete
                            options={customers}
                            getOptionLabel={(option) => `${option.name} (${option.customer_code})`}
                            value={selectedCustomer}
                            onChange={(event, newValue) => {
                                setSelectedCustomer(newValue);
                                setError(null);
                                setSuccess(null);
                            }}
                            renderInput={(params) => (
                                <TextField 
                                    {...params} 
                                    label="Search Credit Customer"
                                    placeholder="Type customer name or code"
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            )}
                            renderOption={(props, option) => (
                                <li {...props}>
                                    <Box display="flex" justifyContent="space-between" width="100%">
                                        <Box>
                                            <Typography variant="body1">
                                                {option.name}
                                            </Typography>
                                            <Typography variant="caption" display="block">
                                                {option.customer_code} • {option.phone || 'No phone'}
                                            </Typography>
                                        </Box>
                                        <Chip 
                                            label={formatCurrency(option.outstanding_balance)}
                                            size="small"
                                            color="error"
                                        />
                                    </Box>
                                </li>
                            )}
                            sx={{ mb: 3 }}
                        />

                        {selectedCustomer && (
                            <Box sx={{ mb: 3, p: 2, bgcolor: '#e8f4fd', borderRadius: 1 }}>
                                <Typography variant="subtitle1" gutterBottom>
                                    Customer Details
                                </Typography>
                                <Grid container spacing={1}>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="textSecondary">
                                            Credit Limit:
                                        </Typography>
                                        <Typography variant="body1">
                                            {formatCurrency(selectedCustomer.credit_limit)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="textSecondary">
                                            Available Credit:
                                        </Typography>
                                        <Typography variant="body1" color="success.main">
                                            {formatCurrency(selectedCustomer.available_credit)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="textSecondary">
                                            Outstanding:
                                        </Typography>
                                        <Typography variant="body1" color="error">
                                            {formatCurrency(selectedCustomer.outstanding_balance)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="textSecondary">
                                            Status:
                                        </Typography>
                                        <Chip 
                                            label={selectedCustomer.credit_status.toUpperCase()}
                                            size="small"
                                            color={
                                                selectedCustomer.credit_status === 'active' ? 'success' :
                                                selectedCustomer.credit_status === 'over_limit' ? 'error' : 'warning'
                                            }
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        )}

                        <Typography variant="h6" gutterBottom>
                            Payment Details
                        </Typography>
                        
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Payment Amount (LKR)"
                                    type="number"
                                    value={paymentData.amount}
                                    onChange={(e) => setPaymentData(prev => ({ ...prev, amount: e.target.value }))}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">LKR</InputAdornment>,
                                    }}
                                    helperText={selectedCustomer ? `Outstanding: ${formatCurrency(selectedCustomer.outstanding_balance)}` : ''}
                                />
                            </Grid>
                            
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Payment Method"
                                    value={paymentData.payment_method}
                                    onChange={(e) => setPaymentData(prev => ({ ...prev, payment_method: e.target.value }))}
                                >
                                    <MenuItem value="cash">Cash</MenuItem>
                                    <MenuItem value="cheque">Cheque</MenuItem>
                                    <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                                    <MenuItem value="card">Credit/Debit Card</MenuItem>
                                    <MenuItem value="online">Online Payment</MenuItem>
                                </TextField>
                            </Grid>
                            
                            {paymentData.payment_method === 'cheque' && (
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Cheque Number"
                                        value={paymentData.cheque_number}
                                        onChange={(e) => setPaymentData(prev => ({ ...prev, cheque_number: e.target.value }))}
                                    />
                                </Grid>
                            )}
                            
                            {(paymentData.payment_method === 'bank_transfer' || paymentData.payment_method === 'online') && (
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Bank Name"
                                        value={paymentData.bank_name}
                                        onChange={(e) => setPaymentData(prev => ({ ...prev, bank_name: e.target.value }))}
                                    />
                                </Grid>
                            )}
                            
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Description (Optional)"
                                    multiline
                                    rows={2}
                                    value={paymentData.description}
                                    onChange={(e) => setPaymentData(prev => ({ ...prev, description: e.target.value }))}
                                    placeholder="e.g., Payment for invoice #1234"
                                />
                            </Grid>
                        </Grid>
                        
                        <Box sx={{ mt: 3, textAlign: 'center' }}>
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={<PaymentIcon />}
                                onClick={handlePaymentSubmit}
                                disabled={loading || !selectedCustomer || !paymentData.amount}
                                sx={{ px: 4, py: 1.5 }}
                            >
                                {loading ? <CircularProgress size={24} /> : 'Record Payment'}
                            </Button>
                        </Box>
                    </Paper>
                </Grid>

                {/* Right Column - Recent Transactions */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, height: '100%' }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h6">
                                Recent Transactions
                            </Typography>
                            {selectedCustomer && (
                                <Button
                                    size="small"
                                    startIcon={<RefreshIcon />}
                                    onClick={fetchCustomerDetails}
                                    disabled={loadingCustomer}
                                >
                                    Refresh
                                </Button>
                            )}
                        </Box>
                        
                        {!selectedCustomer ? (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <CreditCardIcon sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
                                <Typography color="textSecondary">
                                    Select a customer to view their transaction history
                                </Typography>
                            </Box>
                        ) : loadingCustomer ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Type</TableCell>
                                            <TableCell align="right">Amount</TableCell>
                                            <TableCell align="center">Status</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {transactions.map((transaction) => (
                                            <TableRow key={transaction.id} hover>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {new Date(transaction.created_at).toLocaleDateString()}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip 
                                                        label={transaction.transaction_type.toUpperCase()}
                                                        size="small"
                                                        color={
                                                            transaction.transaction_type === 'payment' ? 'success' :
                                                            transaction.transaction_type === 'sale' ? 'primary' : 'default'
                                                        }
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography 
                                                        variant="body2"
                                                        color={transaction.transaction_type === 'payment' ? 'success.main' : 'error'}
                                                    >
                                                        {transaction.transaction_type === 'payment' ? '+' : ''}
                                                        {transaction.amount_lkr || formatCurrency(transaction.amount)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip 
                                                        label={transaction.status}
                                                        size="small"
                                                        color={
                                                            transaction.status === 'paid' ? 'success' :
                                                            transaction.status === 'pending' ? 'warning' : 'default'
                                                        }
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        
                                        {transactions.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                                    <Typography color="textSecondary">
                                                        No transactions found
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                        
                        {selectedCustomer && transactions.length > 0 && (
                            <Box sx={{ mt: 2, textAlign: 'center' }}>
                                <Button
                                    size="small"
                                    onClick={() => {/* Navigate to full transaction history */}}
                                >
                                    View All Transactions
                                </Button>
                            </Box>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            {/* Receipt Dialog */}
            <Dialog open={receiptDialogOpen} onClose={() => setReceiptDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                        <CheckCircleIcon color="success" />
                        Payment Successful
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {lastPayment && (
                        <Box sx={{ pt: 2 }}>
                            <Typography variant="body1" gutterBottom>
                                Payment has been recorded successfully.
                            </Typography>
                            
                            <Box sx={{ my: 3, p: 2, bgcolor: '#e8f5e8', borderRadius: 1 }}>
                                <Typography variant="h6" align="center" color="success.main">
                                    {formatCurrency(paymentData.amount)}
                                </Typography>
                                <Typography variant="body2" align="center">
                                    Payment received from {lastPayment.customer?.name}
                                </Typography>
                            </Box>
                            
                            <Grid container spacing={1}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="textSecondary">
                                        Reference:
                                    </Typography>
                                    <Typography variant="body2">
                                        {lastPayment.transaction?.reference_number || 'N/A'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="textSecondary">
                                        Date:
                                    </Typography>
                                    <Typography variant="body2">
                                        {new Date().toLocaleString()}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="textSecondary">
                                        New Balance:
                                    </Typography>
                                    <Typography variant="h6" color="primary">
                                        {lastPayment.credit_summary?.outstanding_balance || formatCurrency(
                                            parseFloat(selectedCustomer?.outstanding_balance || 0) - parseFloat(paymentData.amount)
                                        )}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReceiptDialogOpen(false)}>
                        Close
                    </Button>
                    <Button 
                        variant="contained" 
                        onClick={printReceipt}
                        startIcon={<ReceiptIcon />}
                    >
                        Print Receipt
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default PaymentCollection;